import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { IMAGES, api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Briefcase } from "lucide-react";
import Seo from "@/components/Seo";
import PasswordInput from "@/components/PasswordInput";
import MathCaptcha from "@/components/MathCaptcha";

const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => (x?.msg ? x.msg : JSON.stringify(x))).join(" ");
  return typeof d === "object" && d?.msg ? d.msg : String(d);
};

const PasswordForm = ({ role, testidPrefix }) => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [captchaOk, setCaptchaOk] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) return toast.error("Email and password are required");
    if (!captchaOk) return toast.error("Captcha is incorrect. Please try again.");
    setLoading(true);
    try {
      const res = await api.post("/auth/login", { ...form, expected_role: role });
      setUser(res.data.user);
      toast.success(`Signed in as ${res.data.user.role}`);
      navigate("/dashboard", { replace: true });
    } catch (e) {
      toast.error(errText(e));
    } finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid={`${testidPrefix}-form`}>
      <div>
        <Label>Company email</Label>
        <Input type="email" autoComplete="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid={`${testidPrefix}-email`} />
      </div>
      <div>
        <Label>Password</Label>
        <PasswordInput autoComplete="current-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid={`${testidPrefix}-password`} eyeTestid={`${testidPrefix}-password-eye`} />
      </div>
      <MathCaptcha onValid={setCaptchaOk} testidPrefix={`${testidPrefix}-captcha`} />
      <button type="submit" className="btn-primary w-full justify-center" disabled={loading} data-testid={`${testidPrefix}-submit`}>
        {loading ? <Loader2 size={14} className="animate-spin" /> : null} {loading ? "Signing in..." : "Sign in"}
      </button>
    </form>
  );
};

/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */
export default function Login() {
  return (
    <div className="grid lg:grid-cols-2 min-h-[calc(100vh-160px)]" data-testid="login-page">
      <Seo path="/login" title="Sign in" description="Sign in to your Ravemora portal." noindex={true} />
      <div className="hidden lg:block relative">
        <img src={IMAGES.handshake} alt="Partnership" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-navy/70 grain" />
        <div className="relative z-10 p-16 h-full flex flex-col justify-between text-offwhite">
          <div>
            <div className="serif-display text-4xl">Ravemora<span className="text-gold">.</span></div>
            <div className="label-eyebrow text-gold mt-2">Portal Access</div>
          </div>
          <p className="serif-display text-3xl max-w-md leading-tight">"A single portal, three practices, one relationship."</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-10 bg-offwhite">
        <div className="max-w-md w-full">
          <div className="label-eyebrow text-navy/60">Sign in</div>
          <h1 className="serif-display text-4xl md:text-5xl mt-4 leading-tight">Welcome back.</h1>

          <Tabs defaultValue="client" className="mt-10">
            <TabsList className="grid grid-cols-2 w-full bg-slateBg">
              <TabsTrigger value="client" data-testid="tab-client" className="flex items-center gap-1.5"><Briefcase size={14}/>Client</TabsTrigger>
              <TabsTrigger value="staff" data-testid="tab-staff" className="flex items-center gap-1.5"><ShieldCheck size={14}/>Staff</TabsTrigger>
            </TabsList>

            <TabsContent value="client" className="mt-8">
              <p className="text-navy/70">Client organizations sign in with the credentials issued by your Ravemora account manager.</p>
              <div className="mt-6"><PasswordForm role="client" testidPrefix="client" /></div>
              <div className="mt-4 text-xs text-navy/50">No account? Write to info@ravemora.com to onboard your organisation.</div>
            </TabsContent>

            <TabsContent value="staff" className="mt-8">
              <p className="text-navy/70">Ravemora internal staff sign in with their company email and password.</p>
              <div className="mt-6"><PasswordForm role="admin" testidPrefix="staff" /></div>
              <div className="mt-4 text-xs text-navy/50">Trouble signing in? Contact your admin.</div>
            </TabsContent>
          </Tabs>

          <div className="mt-8 pt-6 border-t border-navy/10">
            <div className="label-eyebrow text-navy/60">Looking for open roles?</div>
            <p className="mt-2 text-navy/70 text-sm">
              Applying for a job doesn't require an account. Browse and apply directly on the{" "}
              <Link to="/careers" className="text-gold hover:underline font-medium" data-testid="login-to-accelerator">
                Open Positions page
              </Link>.
            </p>
          </div>

          <div className="mt-8 label-eyebrow text-navy/50">By continuing, you agree to Ravemora's Terms and Privacy Policy.</div>
        </div>
      </div>
    </div>
  );
}
